import { useEffect, useState } from 'react'

import { usePrecomputedEVMGasPrices } from '@src/hooks/evm/usePrecomputedEVMGasPrices'
import { viemChainConfigs } from '@src/hooks/evm/useViemClient'
import { usePrecomputedSolanaGasPrices } from '@src/hooks/solana/usePrecomputedSolanaGasPrices'
import { usePrecomputedTronGasPrices } from '@src/hooks/tron/usePrecomputedTronGasPrices'
import { PrecomputeGasResults } from '@src/types/gasPrices'
import { createContextLogger } from '@src/utils/logger'

// Different chains might need differently adjusted buffers.
export const adjustWrapFee = (fee: bigint, chainId: number) => {
    if (chainId === 1) {
        return BigInt(1) * fee
    }
    if (chainId === 42161) {
        return BigInt(3) * fee
    }
    return BigInt(3) * fee
}

const PRINT_GAS_ERRORS = false
const logger = createContextLogger('usePrecomputedGasPrices')

/**
 * Custom hook to precompute gas prices/fees of transactions
 * @returns Precomputed gas prices
 */
export const usePrecomputedGasPrices = (swapContracts: Record<string, any> | undefined) => {
    const [isComputing, setIsComputing] = useState(false)
    const [isGasPrecomputingDone, setIsGasPrecomputingDone] = useState(false)
    const [precomputedGasPrices, setPrecomputedGasPrices] = useState<PrecomputeGasResults>()

    const { computeEVMGasPrices } = usePrecomputedEVMGasPrices(swapContracts)
    const { computeSolanaGasPrices } = usePrecomputedSolanaGasPrices(swapContracts)
    const { computeTronGasPrices } = usePrecomputedTronGasPrices(swapContracts)

    useEffect(() => {
        setIsComputing(true)
        setIsGasPrecomputingDone(false)
        setPrecomputedGasPrices(undefined)

        const precomputeGasFees = async () => {
            if (!swapContracts) {
                PRINT_GAS_ERRORS && logger.error('swapContracts not found')
                return
            }

            try {
                const [evmResults, solanaResults, tronResults] = await Promise.all([
                    computeEVMGasPrices(),
                    computeSolanaGasPrices(),
                    computeTronGasPrices(),
                ])

                const results: PrecomputeGasResults = {
                    ...evmResults,
                    ...solanaResults,
                    ...tronResults,
                }

                logger.info('Precomputed gas fee results:', results)
                setPrecomputedGasPrices(results)
            } catch (error) {
                PRINT_GAS_ERRORS && logger.error('Error computing gas prices:', error)
            } finally {
                setIsComputing(false)
                setIsGasPrecomputingDone(true)
            }
        }

        precomputeGasFees()
    }, [swapContracts, viemChainConfigs])

    return { precomputedGasPrices, isComputing, isGasPrecomputingDone }
}
